// ═══ API SERVICE — Connects frontend to backend ═══
// All API calls go through this service.
// Handles JWT tokens, company ID header, error handling

const API_BASE = import.meta.env.VITE_API_URL || '/api/v1';

// Read a JWT's payload without verifying it. Decoding is not validation — the
// server still checks the signature — but it lets us see `exp` and refresh an
// expired token BEFORE spending a request we know will come back 401.
const decodeJwtPayload = (token) => {
  try {
    const part = String(token || '').split('.')[1];
    if (!part) return null;
    const b64 = part.replace(/-/g, '+').replace(/_/g, '/');
    const pad = b64.length % 4 ? '='.repeat(4 - (b64.length % 4)) : '';
    return JSON.parse(atob(b64 + pad));
  } catch {
    return null;
  }
};

class ApiService {
  constructor() {
    this.token = localStorage.getItem('cms_token') || null;
    this.refreshToken = localStorage.getItem('cms_refresh_token') || null;
    this.companyId = localStorage.getItem('cms_company_id') || null;
    this.maxConcurrent = 6;
    this._active = 0;
    this._waiting = [];
    this.refreshPromise = null;
  }

  isAccessTokenExpired(skewMs = 30000) {
    if (!this.token) return false;
    const payload = decodeJwtPayload(this.token);
    // Unreadable or no `exp` — say nothing and let the 401 path handle it.
    if (!payload || typeof payload.exp !== 'number') return false;
    return Date.now() >= payload.exp * 1000 - skewMs;
  }

  // Refresh only if the token is actually expired. Returns the user object when
  // the server sent one, otherwise null. Check `api.token` afterwards — a failed
  // refresh clears auth.
  async ensureFreshToken() {
    if (!this.refreshToken || !this.isAccessTokenExpired()) return null;
    return this.refreshAccessToken();
  }

  // ── Set auth tokens ──
  setTokens(token, refreshToken) {
    this.token = token;
    this.refreshToken = refreshToken;
    localStorage.setItem('cms_token', token);
    if (refreshToken) localStorage.setItem('cms_refresh_token', refreshToken);
  }

  setCompanyId(id) {
    this.companyId = id;
    localStorage.setItem('cms_company_id', id);
  }

  clearAuth() {
    this.token = null;
    this.refreshToken = null;
    this.companyId = null;
    localStorage.removeItem('cms_token');
    localStorage.removeItem('cms_refresh_token');
    localStorage.removeItem('cms_company_id');
  }

  // ── Base fetch with auth headers ──
  async request(method, path, body = null, options = {}) {
    await this._acquire();
    try {
      return await this._send(method, path, body, options);
    } finally {
      this._release();
    }
  }

  async _send(method, path, body = null, options = {}) {
    // Refresh up front when the access token has already expired. The old flow
    // issued the request anyway, took a 401, refreshed, then retried — three round
    // trips (six with preflights) where one will do. The 401 path below stays as a
    // fallback for cases we cannot see client-side: clock skew, a revoked user, a
    // rotated signing secret.
    if (path !== '/auth/refresh') {
      const hadToken = !!this.token;
      await this.ensureFreshToken();
      // Refresh failed and cleared auth. Sending the request now would just produce
      // a "No token provided" 401, so fail with something the caller can act on.
      if (hadToken && !this.token) throw new Error('Session expired — please sign in again.');
    }

    const headers = {
      'Content-Type': 'application/json',
      ...(this.token ? { 'Authorization': `Bearer ${this.token}` } : {}),
      ...(this.companyId ? { 'X-Company-ID': this.companyId } : {}),
      ...options.headers,
    };

    const config = { method, headers };
    if (body && method !== 'GET') {
      config.body = JSON.stringify(body);
    }

    try {
      let response = await fetch(`${API_BASE}${path}`, config);

      // Auto-refresh on 401
      if (response.status === 401 && this.refreshToken && path !== '/auth/refresh') {
        await this.refreshAccessToken();
        if (this.token) {
          headers['Authorization'] = `Bearer ${this.token}`;
          response = await fetch(`${API_BASE}${path}`, { ...config, headers });
        }
      }

      const data = await response.json();
      if (!response.ok) {
        const e = new Error(data.error || `HTTP ${response.status}`);
        e.status = response.status;
        // 409 from /payment-apps: [{ key, claimKey?, claimedBy: { appNumber, ... } }]
        if (Array.isArray(data.conflicts)) e.conflicts = data.conflicts;
        throw e;
      }
      return data;
    } catch (err) {
      console.error(`API Error [${method} ${path}]:`, err.message);
      throw err;
    }
  }

  // Single-flight. Concurrent callers share one request — otherwise an expired
  // token with N requests in flight fires N refreshes at once. The 500ms stagger
  // used to hide that by never letting two requests overlap; now that requests do
  // overlap, this is what keeps it to one refresh.
  refreshAccessToken() {
    if (this.refreshPromise) return this.refreshPromise;
    this.refreshPromise = this._performRefresh()
      .finally(() => { this.refreshPromise = null; });
    return this.refreshPromise;
  }

  // Returns the user object when the server sent one, otherwise null.
  // On failure it clears auth, so callers should check `this.token`.
  async _performRefresh() {
    try {
      const res = await fetch(`${API_BASE}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken: this.refreshToken }),
      });
      const data = await res.json();
      if (data.success && data.data && data.data.token) {
        this.setTokens(data.data.token, this.refreshToken);
        // Present since /auth/refresh started returning the user it already loaded.
        // Null against an older API, which just means /auth/me still runs.
        return data.data.user || null;
      }
      this.clearAuth();
      return null;
    } catch {
      this.clearAuth();
      return null;
    }
  }

  // ── Multipart upload (photos, files) ──
  async upload(path, file, fields = {}) {
    const formData = new FormData();
    formData.append('file', file);
    Object.entries(fields).forEach(([k, v]) => formData.append(k, v));

    const response = await fetch(`${API_BASE}${path}`, {
      method: 'POST',
      headers: {
        ...(this.token ? { 'Authorization': `Bearer ${this.token}` } : {}),
        ...(this.companyId ? { 'X-Company-ID': this.companyId } : {}),
      },
      body: formData,
    });
    return response.json();
  }

  // Requests issued but not yet settled (in flight + queued). The UI reads this
  // to tell "more responses are still coming" from "the burst is over", so it can
  // commit one render for the burst instead of one per response.
  get pendingCount() { return this._active + this._waiting.length; }

  _acquire() {
    if (this._active < this.maxConcurrent) { this._active++; return Promise.resolve(); }
    return new Promise(resolve => this._waiting.push(resolve));
  }

  _release() {
    // Hand the slot straight to the next waiter rather than decrementing and
    // letting it re-race; _active stays accurate either way.
    const next = this._waiting.shift();
    if (next) next();
    else this._active--;
  }

  // ── Convenience methods ──
  get(path) { return this.request('GET', path); }
  post(path, body) { return this.request('POST', path, body); }
  put(path, body) { return this.request('PUT', path, body); }
  del(path) { return this.request('DELETE', path); }

  // ═══ AUTH ═══
  async login(email, password) {
    const res = await this.post('/auth/login', { email, password });
    if (res.success) {
      this.setTokens(res.data.token, res.data.refreshToken);
      const companies = JSON.parse(res.data.user.companyAccess || '[]');
      if (companies.length > 0) this.setCompanyId(companies[0]);
    }
    return res;
  }

  async register(data) { return this.post('/auth/register', data); }
  async getMe() { return this.get('/auth/me'); }

  // ═══ COMPANIES ═══
  async getCompanies() { return this.get('/companies'); }
  async getCompany(id) { return this.get(`/companies/${id}`); }
  async updateCompany(id, data) { return this.put(`/companies/${id}`, data); }
  async createCompany(data) { return this.post('/companies', data); }
  async deleteCompany(id) { return this.del(`/companies/${id}`); }
  //  BY Aaryan:- added 132 and 133 createCompany and deleteCompany methods to
  //  src/utils/api.js so the frontend can call your /companies POST/DELETE endpoints

  // ═══ PROJECTS ═══
  async getProjects() { return this.get('/projects'); }
  async createProject(data) { return this.post('/projects', data); }
  async getProject(id) { return this.get(`/projects/${id}`); }
  async updateProject(id, data) { return this.put(`/projects/${id}`, data); }
  async deleteProject(id) { return this.del(`/projects/${id}`); }
  async getProjectProperties(id) { return this.get(`/projects/${id}/properties`); }

  // ═══ PROPERTIES ═══
  // Property creation must go to the generic /properties endpoint,
  // with projectId included in the request body.
  async createProperty(projectId, data) { return this.post(`/properties`, { ...data, projectId }); }
  async getProperty(id) { return this.get(`/properties/${id}`); }
  async updateProperty(id, data) { return this.put(`/properties/${id}`, data); }
  async deleteProperty(id) { return this.del(`/properties/${id}`); }
  async updatePropertyLocation(id, lat, lng) { return this.put(`/properties/${id}/location`, { lat, lng }); }
  async updatePropertyStatus(id, status) { return this.put(`/properties/${id}/status`, { status }); }
  async updateTrackerTask(propertyId, taskKey, data) { return this.put(`/properties/${propertyId}/tracker/${taskKey}`, data); }
  async addPropertyTask(propertyId, data) { return this.post(`/properties/${propertyId}/tasks`, data); }
  async uploadPropertyPhoto(propertyId, file, category) { return this.upload(`/properties/${propertyId}/photos`, file, { category }); }

  // ═══ WORKFORCE ═══
  async getSubcontractors() { return this.get('/subcontractors'); }
  async createSubcontractor(data) { return this.post('/subcontractors', data); }
  async updateSubcontractor(id, data) { return this.put(`/subcontractors/${id}`, data); }
  async deleteSubcontractor(id) { return this.del(`/subcontractors/${id}`); }
  // The list carries only `hasPhoto`; the image (a small data URL) is fetched when it is shown.
  async getSubcontractorPhoto(id) { return this.get(`/subcontractors/${id}/photo`); }
  async getSubcontractorEvaluations(id) { return this.get(`/subcontractors/${id}/evaluations`); }
  async addSubcontractorEvaluation(id, data) { return this.post(`/subcontractors/${id}/evaluations`, data); }
  async deleteSubcontractorEvaluation(id, evalId) { return this.del(`/subcontractors/${id}/evaluations/${evalId}`); }
  async getOperatives() { return this.get('/operatives'); }
  // The list carries only `hasPhoto`; the image (a small data URL) is fetched when it is shown.
  async getOperativePhoto(id) { return this.get(`/operatives/${id}/photo`); }
  async createOperative(data) { return this.post('/operatives', data); }
  async updateOperative(id, data) { return this.put(`/operatives/${id}`, data); }
  async deleteOperative(id) { return this.del(`/operatives/${id}`); }
  async getOperativeEvaluations(id) { return this.get(`/operatives/${id}/evaluations`); }
  async addOperativeEvaluation(id, data) { return this.post(`/operatives/${id}/evaluations`, data); }
  async deleteOperativeEvaluation(id, evalId) { return this.del(`/operatives/${id}/evaluations/${evalId}`); }
  
  // ═══ TIMESHEETS ═══
  async getTimesheets() { return this.get('/timesheets'); }
  async createTimesheet(data) { return this.post('/timesheets', data); }
  async approveTimesheet(id) { return this.put(`/timesheets/${id}/approve`); }
  async deleteTimesheet(id) { return this.del(`/timesheets/${id}`); }
  async getTimesheetSummary(weekStart) { return this.get(`/timesheets/summary/weekly?weekStart=${weekStart}`); }

  // ═══ CLOCK IN/OUT ═══
  async getClockIns() { return this.get('/clock-ins'); }
  async createClockIn(data) { return this.post('/clock-ins', data); }
  async updateClockIn(id, data) { return this.put(`/clock-ins/${id}`, data); }
  async deleteClockIn(id) { return this.del(`/clock-ins/${id}`); }

  // ═══ SNAGS ═══
  async getSnags() { return this.get('/snags'); }
  async createSnag(data) { return this.post('/snags', data); }
  async updateSnag(id, data) { return this.put(`/snags/${id}`, data); }
  async deleteSnag(id) { return this.del(`/snags/${id}`); }

  // ═══ SITE DIARY ═══
  async getSiteDiary() { return this.get('/site-diary'); }
  async createSiteDiary(data) { return this.post('/site-diary', data); }
  async updateSiteDiary(id, data) { return this.put(`/site-diary/${id}`, data); }
  async deleteSiteDiary(id) { return this.del(`/site-diary/${id}`); }

  // ═══ LETTERS ═══
  async getLetters() { return this.get('/letters'); }
  async createLetter(data) { return this.post('/letters', data); }
  async updateLetter(id, data) { return this.put(`/letters/${id}`, data); }
  async deleteLetter(id) { return this.del(`/letters/${id}`); }

  // ═══ EQUIPMENT REGISTERS ═══
  async getAccessEquipment() { return this.get('/access-equipment'); }
  async createAccessEquipment(data) { return this.post('/access-equipment', data); }
  async updateAccessEquipment(id, data) { return this.put(`/access-equipment/${id}`, data); }
  async deleteAccessEquipment(id) { return this.del(`/access-equipment/${id}`); }
  async getOfficeEquipment() { return this.get('/office-equipment'); }
  async createOfficeEquipment(data) { return this.post('/office-equipment', data); }
  async updateOfficeEquipment(id, data) { return this.put(`/office-equipment/${id}`, data); }
  async deleteOfficeEquipment(id) { return this.del(`/office-equipment/${id}`); }

  // ═══ INVOICE / QUOTE TEMPLATES ═══
  async getInvoiceTemplates() { return this.get('/invoice-templates'); }
  async createInvoiceTemplate(data) { return this.post('/invoice-templates', data); }
  async updateInvoiceTemplate(id, data) { return this.put(`/invoice-templates/${id}`, data); }
  async deleteInvoiceTemplate(id) { return this.del(`/invoice-templates/${id}`); }
  async getQuoteTemplates() { return this.get('/quote-templates'); }
  async createQuoteTemplate(data) { return this.post('/quote-templates', data); }
  async updateQuoteTemplate(id, data) { return this.put(`/quote-templates/${id}`, data); }
  async deleteQuoteTemplate(id) { return this.del(`/quote-templates/${id}`); }
  async getActiveClockIns() { return this.get('/clock-ins/active/now'); }
  async forceClockOut(id) { return this.put(`/clock-ins/${id}/force-out`); }

  // ═══ FINANCIAL ═══
  async getInvoices() { return this.get('/invoices'); }
  async createInvoice(data) { return this.post('/invoices', data); }
  async updateInvoice(id, data) { return this.put(`/invoices/${id}`, data); }
  async deleteInvoice(id) { return this.del(`/invoices/${id}`); }
  async getQuotes() { return this.get('/quotes'); }
  async createQuote(data) { return this.post('/quotes', data); }
  async updateQuote(id, data) { return this.put(`/quotes/${id}`, data); }
  async deleteQuote(id) { return this.del(`/quotes/${id}`); }
  async getEstimates() { return this.get('/estimates'); }
  async createEstimate(data) { return this.post('/estimates', data); }
  async updateEstimate(id, data) { return this.put(`/estimates/${id}`, data); }
  async deleteEstimate(id) { return this.del(`/estimates/${id}`); }
  async convertEstimateToQuote(id) { return this.post(`/estimates/${id}/convert`); }
  async getPurchaseOrders() { return this.get('/purchase-orders'); }
  async createPurchaseOrder(data) { return this.post('/purchase-orders', data); }
  async updatePurchaseOrder(id, data) { return this.put(`/purchase-orders/${id}`, data); }
  async deletePurchaseOrder(id) { return this.del(`/purchase-orders/${id}`); }
  async getPaymentApps() { return this.get('/payment-apps'); }
  async getPaymentApplications() { return this.getPaymentApps(); } // Alias for CMS.jsx
  async createPaymentApp(data) { return this.post('/payment-apps', data); }
  async createPaymentApplication(data) { return this.createPaymentApp(data); } // Alias for CMS.jsx
  async updatePaymentApp(id, data) { return this.put(`/payment-apps/${id}`, data); }
  async updatePaymentApplication(id, data) { return this.updatePaymentApp(id, data); } // Alias for CMS.jsx
  async deletePaymentApp(id) { return this.del(`/payment-apps/${id}`); }
  async deletePaymentApplication(id) { return this.deletePaymentApp(id); } // Alias for CMS.jsx
  async getVariationOrders() { return this.get('/variation-orders'); }
  async createVariationOrder(data) { return this.post('/variation-orders', data); }
  async updateVariationOrder(id, data) { return this.put(`/variation-orders/${id}`, data); }
  async deleteVariationOrder(id) { return this.del(`/variation-orders/${id}`); }

  // ═══ CONTACTS ═══
  async getClients() { return this.get('/clients'); }
  async createClient(data) { return this.post('/clients', data); }
  async updateClient(id, data) { return this.put(`/clients/${id}`, data); }
  async deleteClient(id) { return this.del(`/clients/${id}`); }
  async getSuppliers() { return this.get('/suppliers'); }
  async createSupplier(data) { return this.post('/suppliers', data); }
  async updateSupplier(id, data) { return this.put(`/suppliers/${id}`, data); }
  async deleteSupplier(id) { return this.del(`/suppliers/${id}`); }
  async getSupplierPrices(id) { return this.get(`/suppliers/${id}/prices`); }
  async createSupplierPrice(id, data) { return this.post(`/suppliers/${id}/prices`, data); }
  async getSupplierEvaluations(id) { return this.get(`/suppliers/${id}/evaluations`); }
  async addSupplierEvaluation(id, data) { return this.post(`/suppliers/${id}/evaluations`, data); }
  async deleteSupplierEvaluation(id, evalId) { return this.del(`/suppliers/${id}/evaluations/${evalId}`); }
  //  BY Aaryan:- added 197-98 and 201-202 update and delete clients/supplier methods to
  //  src/utils/api.js so the frontend can call your /companies POST/DELETE endpoints

  // ═══ ASSETS ═══
  async getVehicles() { return this.get('/vehicles'); }
  async createVehicle(data) { return this.post('/vehicles', data); }
  async updateVehicle(id, data) { return this.put(`/vehicles/${id}`, data); }
  async deleteVehicle(id) { return this.del(`/vehicles/${id}`); }
  async getTools() { return this.get('/tools'); }
  async createTool(data) { return this.post('/tools', data); }
  async updateTool(id, data) { return this.put(`/tools/${id}`, data); }
  async deleteTool(id) { return this.del(`/tools/${id}`); }

  // ═══ PEOPLE DOCS ═══
  async getPersonDocs() { return this.get('/person-documents'); }
  // The list returns metadata only — this is the one call that carries the file
  // itself, so use it lazily (on view/download), never to populate a list.
  async getPersonDoc(id) { return this.get(`/person-documents/${id}`); }
  async createPersonDoc(data) { return this.post('/person-documents', data); }
  async updatePersonDoc(id, data) { return this.put(`/person-documents/${id}`, data); }
  async deletePersonDoc(id) { return this.del(`/person-documents/${id}`); }
    
  // ═══ SMS & WHATSAPP ═══
  async sendSms(data) { return this.post('/sms/send', data); }
  // Queue a text for 15:00 London the day before a visit. The server works out the
  // send date and rejects it if that time has already passed.
  async scheduleSms(data) { return this.post('/sms/schedule', data); }
  // Cancel the pending text for a tracker booking: { propertyId, source: 'tracker', taskKey }.
  async cancelScheduledSms(data) { return this.post('/sms/schedule/cancel', data); }
  async getSmsHistory() { return this.get('/sms/history'); }
  async deleteSmsHistory(id) { return this.del(`/sms/history/${id}`); }
  async getSmsGroups() { return this.get('/sms/groups'); }
  async createSmsGroup(data) { return this.post('/sms/groups', data); }
  async updateSmsGroup(id, data) { return this.put(`/sms/groups/${id}`, data); }
  async deleteSmsGroup(id) { return this.del(`/sms/groups/${id}`); }
  async getSmsTemplates() { return this.get('/sms/templates'); }
  async createSmsTemplate(data) { return this.post('/sms/templates', data); }
  async updateSmsTemplate(id, data) { return this.put(`/sms/templates/${id}`, data); }
  async deleteSmsTemplate(id) { return this.del(`/sms/templates/${id}`); }
  // ── WhatsApp: read ──
  async getWhatsappInbox() { return this.get('/whatsapp/inbox'); }
  async getWhatsappConversations() { return this.get('/whatsapp/conversations'); }
  async getWhatsappTemplates() { return this.get('/whatsapp/templates'); }
  // ── WhatsApp: write / send ──
  async sendWhatsapp(data) { return this.post('/whatsapp/send', data); }
  async broadcastWhatsapp(data) { return this.post('/whatsapp/broadcast', data); }
  async logIncomingWhatsapp(data) { return this.post('/whatsapp/inbox', data); }
  // ── WhatsApp: inbox actions ──
  async markWhatsappRead(id, read = true) { return this.put(`/whatsapp/inbox/${id}/read`, { read }); }
  async markAllWhatsappRead() { return this.put('/whatsapp/inbox/read-all', {}); }
  async starWhatsapp(id, starred = true) { return this.put(`/whatsapp/inbox/${id}/star`, { starred }); }
  async deleteWhatsapp(id) { return this.del(`/whatsapp/inbox/${id}`); }

  // ═══ COMPLIANCE ═══
  async getPolicies() { return this.get('/compliance/policies'); }
  async createPolicy(data) { return this.post('/compliance/policies', data); }
  async updatePolicy(id, data) { return this.put(`/compliance/policies/${id}`, data); }
  async deletePolicy(id) { return this.del(`/compliance/policies/${id}`);}
  async getRams() { return this.get('/compliance/rams'); }
  async createRams(data) { return this.post('/compliance/rams', data); }
  async updateRams(id, data) { return this.put(`/compliance/rams/${id}`, data);}
  async deleteRams(id) { return this.del(`/compliance/rams/${id}`); }
  async getToolboxTalks() { return this.get('/compliance/toolbox-talks'); }
  async createToolboxTalk(data) { return this.post('/compliance/toolbox-talks', data); }
  async updateToolboxTalk(id, data) { return this.put(`/compliance/toolbox-talks/${id}`, data); }
  async deleteToolboxTalk(id) { return this.del(`/compliance/toolbox-talks/${id}`); }
  async signToolboxTalk(id, data) { return this.put(`/compliance/toolbox-talks/${id}/sign`, data); }
  async createToolboxSignLink(id) { return this.post(`/compliance/toolbox-talks/${id}/sign-link`); }
  async getTrainingMatrix() { return this.get('/compliance/training'); }
  async createTrainingMatrix(data) { return this.post('/compliance/training', data); }
  async updateTrainingMatrix(id, data) { return this.put(`/compliance/training/${id}`, data); }
  async deleteTrainingMatrix(id) { return this.del(`/compliance/training/${id}`); }
  async getAccreditations() { return this.get('/compliance/accreditations'); }
  async createAccreditation(data) { return this.post('/compliance/accreditations', data); }
  async updateAccreditation(id, data) { return this.put(`/compliance/accreditations/${id}`, data); }
  async deleteAccreditation(id) { return this.del(`/compliance/accreditations/${id}`); }
  async getComplianceDocs() { return this.get('/compliance/documents'); }
  async createComplianceDoc(data) { return this.post('/compliance/documents', data); }
  async getSiteInspections() { return this.get('/compliance/inspections'); }
  async createSiteInspection(data) { return this.post('/compliance/inspections', data); }
  async updateSiteInspection(id, data) { return this.put(`/compliance/inspections/${id}`, data); }
  async deleteSiteInspection(id) { return this.del(`/compliance/inspections/${id}`); }
  async getAgreements() { return this.get('/compliance/agreements'); }
  async createAgreement(data) { return this.post('/compliance/agreements', data); }
  async updateAgreement(id, data) { return this.put(`/compliance/agreements/${id}`, data); }
  async updateAgreementStatus(id, status, signedBy, date) { return this.put(`/compliance/agreements/${id}/status`, { status, ...(signedBy !== undefined ? { signedBy } : {}), ...(date ? { date } : {}) }); }
  async getIncidents() { return this.get('/compliance/incidents'); }
  async createIncident(data) { return this.post('/compliance/incidents', data); }
  async updateIncident(id, data) { return this.put(`/compliance/incidents/${id}`, data); }
  async deleteIncident(id) { return this.del(`/compliance/incidents/${id}`); }

  // ═══ HMRC ═══
  async hmrcConnect() { return this.get('/hmrc/connect'); }
  async cisVerify(subId) { return this.post('/hmrc/cis/verify', { subcontractorId: subId }); }
  async getCisReturn(month) { return this.get(`/hmrc/cis/return?month=${month}`); }
  async submitCisReturn(data) { return this.post('/hmrc/cis/return', data); }
  async getCisReturns() { return this.get('/hmrc/cis/returns'); }
  async calculateVatReturn(from, to) { return this.get(`/hmrc/vat/calculate?from=${from}&to=${to}`); }
  async submitVatReturn(data) { return this.post('/hmrc/vat/return', data); }
  async getVatReturns() { return this.get('/hmrc/vat/returns'); }
  async getVatObligations() { return this.get('/hmrc/vat/obligations'); }

  // ═══ BANKING ═══
  async getSupportedBanks() { return this.get('/banking/banks'); }
  async getBankConnections() { return this.get('/banking/connections'); }
  async connectBank(data) { return this.post('/banking/connect', data); }
  async syncBankTransactions(connectionId) { return this.post(`/banking/connections/${connectionId}/sync`); }
  async getBankBalance(connectionId) { return this.get(`/banking/connections/${connectionId}/balance`); }
  async getBankTransactions(params = {}) {
    const qs = new URLSearchParams(params).toString();
    return this.get(`/banking/transactions${qs ? '?' + qs : ''}`);
  }
  async reconcileTransaction(id, reconciledWith) { return this.put(`/banking/transactions/${id}/reconcile`, { reconciledWith }); }
  async autoReconcile() { return this.post('/banking/auto-reconcile'); }
  async disconnectBank(id) { return this.del(`/banking/connections/${id}`); }
  async getPaymentRecords() { return this.get('/banking/payments'); }
  async recordPayment(data) { return this.post('/banking/payments', data); }

  // ═══ COMMS & CALENDAR ═══
  async getCommLog(params = {}) {
    const qs = new URLSearchParams(params).toString();
    return this.get(`/comm-log${qs ? '?' + qs : ''}`);
  }
  async createCommLog(data) { return this.post('/comm-log', data); }
  async updateCommLog(id, data) { return this.put(`/comm-log/${id}`, data); }
  async deleteCommLog(id) { return this.del(`/comm-log/${id}`); }

  async getCalendarEvents(params = {}) {
    const qs = new URLSearchParams(params).toString();
    return this.get(`/calendar${qs ? '?' + qs : ''}`);
  }
  async createCalendarEvent(data) { return this.post('/calendar', data); }
  async updateCalendarEvent(id, data) { return this.put(`/calendar/${id}`, data); }
  async deleteCalendarEvent(id) { return this.del(`/calendar/${id}`); }
  // Explicit cancellation (NOT deletion): the appointment stays, marked cancelled, and anyone who was
  // already sent a reminder is told. Safe to repeat: an already-cancelled event answers
  // { alreadyCancelled: true } and nothing changes. `reason` is internal and never sent to anyone.
  async cancelCalendarEvent(id, reason) { return this.post(`/calendar/${id}/cancel`, reason ? { reason } : {}); }

  async getTodos() { return this.get('/todos'); }
  async createTodo(data) { return this.post('/todos', data); }
  async updateTodo(id, data) { return this.put(`/todos/${id}`, data); }
  async deleteTodo(id) { return this.del(`/todos/${id}`); }
  async getPqqTemplates() { return this.get('/pqq-templates'); }
  async createPqqTemplate(data) { return this.post('/pqq-templates', data); }
  async updatePqqTemplate(id, data) { return this.put(`/pqq-templates/${id}`, data); }
  async deletePqqTemplate(id) { return this.del(`/pqq-templates/${id}`); }
  async getPqqSubmissions() { return this.get('/pqq-submissions'); }
  async createPqqSubmission(data) { return this.post('/pqq-submissions', data); }
  async updatePqqSubmission(id, data) { return this.put(`/pqq-submissions/${id}`, data); }
  async deletePqqSubmission(id) { return this.del(`/pqq-submissions/${id}`); }
  // Texts the questionnaire link to the number on the recipient's own record.
  // Rejects if there is no number, or if the gateway does not accept the text —
  // the submission is only marked as sent by SMS when one actually went out.
  async sendPqqSms(id) { return this.post(`/pqq/${id}/send-sms`, {}); }
  // Mints the same link the text would carry and returns it, without sending
  // anything. Used by the "Print & Hand" method so the link can be copied and
  // shared by WhatsApp, email or anything else.
  async getPqqLink(id) { return this.post(`/pqq/${id}/link`, {}); }

  async getSchedules(params = {}) {
    const qs = new URLSearchParams(params).toString();
    return this.get(`/schedules${qs ? '?' + qs : ''}`);
  }
  async createSchedule(data) { return this.post('/schedules', data); }
  async updateSchedule(id, data) { return this.put(`/schedules/${id}`, data); }
  async deleteSchedule(id) { return this.del(`/schedules/${id}`); }

  // ═══ ADMIN ═══
  async getUsers() { return this.get('/users'); }
  async createUser(data) { return this.post('/users', data); }
  async updateUser(id, data) { return this.put(`/users/${id}`, data); }
  async deleteUser(id) { return this.del(`/users/${id}`); }
  async getClientPortalAccess() { return this.get('/users/client-access'); }
  async createClientPortalAccess(data) { return this.post('/users/client-access', data); }
  async updateClientPortalAccess(id, data) { return this.put(`/users/client-access/${id}`, data); }
  async deleteClientPortalAccess(id) { return this.del(`/users/client-access/${id}`); }
  async getPortalUsers() { return this.get('/users/portal-users'); }
  async createPortalUser(data) { return this.post('/users/portal-users', data); }
  async updatePortalUser(id, data) { return this.put(`/users/portal-users/${id}`, data); }
  async deletePortalUser(id) { return this.del(`/users/portal-users/${id}`); }
  async portalLogin(data) { return this.post('/portal/login', data); }
  async getAuditLog() { return this.get('/audit-log'); }
  async addAuditEntry(data) { return this.post('/audit-log', data); }
  async getReportFinancial() { return this.get('/reports/financial'); }
  async getReportProjectStatus() { return this.get('/reports/project-status'); }
  async getReportWorkforce() { return this.get('/reports/workforce'); }
  async getReportSnags() { return this.get('/reports/snags'); }

  // ═══ SEARCH ═══
  async globalSearch(q) { return this.get(`/search?q=${encodeURIComponent(q)}`); }
  async addressSearch(q) { return this.get(`/integrations/address-search?q=${encodeURIComponent(q)}`); }

  // ═══ AI ═══
  // async aiChat(systemPrompt, userMessage) { return this.post('/integrations/ai/chat', { systemPrompt, userMessage }); }
  async aiDocReview() { return this.post('/ai/doc-review', {}); }
  async aiReport(type) { return this.post('/ai/report', { type }); }
  async aiChat(message, history = []) { return this.post('/ai/chat', { message, history }); }
  async aiWorkerHelp(message, lang, history = []) { return this.post('/ai/worker-help', { message, lang, history }); }

  // ═══ UPLOAD ═══
  async uploadFile(file, folder) { return this.upload('/upload', file, { folder }); }
  async getPresignedUrl(filename, contentType, folder) { return this.post('/upload/presigned', { filename, contentType, folder }); }

  // ═══ NOTIFICATIONS ═══
  // Read-only. The Notifications page's alerts are computed in the browser; these are the two
  // server records behind it: reminder delivery for one appointment, and the expiry reminders sent.
  async getCalendarReminderStatus() { return this.get('/calendar/reminder-status'); }
  async getCalendarReminderHistory(eventId) { return this.get(`/calendar/${eventId}/reminders`); }
  async getExpiryNotifications(limit = 50) { return this.get(`/expiry-notifications?limit=${limit}`); }

  // ═══ XERO ═══
  // status reports what is actually stored on the server: `connected` means a
  // token exists, `usable` that it also has a tenant id, and `flagged` is the
  // old Company Settings switch, which means nothing on its own.
  async getXeroStatus() { return this.get('/integrations/xero/status'); }
  async getXeroConnectUrl() { return this.get('/integrations/xero/connect'); }
  async disconnectXero() { return this.post('/integrations/xero/disconnect', {}); }

  // ═══ PLATFORM ADMIN ═══
  async getTenants() { return this.get('/admin/tenants'); }
  async createTenant(data) { return this.post('/admin/tenants', data); }
  async updateTenant(id, data) { return this.put(`/admin/tenants/${id}`, data); }
  async deleteTenant(id) { return this.del(`/admin/tenants/${id}`); }
  async getRevenue() { return this.get('/admin/revenue'); }
}

// Singleton export
const api = new ApiService();
export default api;
